export const UAE_ACCESS_LEVELS = ['SIGNED_UP', 'VERIFIED', 'JOINED'] as const;
export type UaeAccessLevel = (typeof UAE_ACCESS_LEVELS)[number];
export type UaeNextStep = 'verify_id' | 'join';
export type UaeGateTrigger = 'locked_action' | 'limit_reached';
export type UaeGateFeature = 'ai' | 'marketing';
export type UaeGateCta = 'verify_id' | 'join' | 'not_now';

// ID-107 (identity upload) and ID-108 (Join flow) own these pages.
export const UAE_VERIFY_ID_ROUTE = '/verify-id';
export const UAE_JOIN_ROUTE = '/join';
// Pricing / billing surfaces a UAE agent is redirected away from.
export const UAE_BLOCKED_ROUTE_PREFIXES = ['/saas', '/profile/team', '/payment'] as const;
export const UAE_REDIRECT_ROUTE = '/dashboard';

export type UaeProgramme = {
  // True once /api/billing/me has answered; before that the session market is the fallback.
  loaded?: boolean;
  isUae: boolean;
  level: UaeAccessLevel | null;
  nextStep: UaeNextStep | null;
  showPricing: boolean;
  // ID-241: owner of a UAE Team plan — may manage their team's seats.
  teamOwner?: boolean;
};

export const NOT_UAE: UaeProgramme = { isUae: false, level: null, nextStep: null, showPricing: true };

export type UaeGateRequest = {
  trigger: UaeGateTrigger;
  nextStep: UaeNextStep | null;
  level?: UaeAccessLevel | null;
  feature?: UaeGateFeature | null;
  action?: string | null;
  limit?: number | null;
};

export type UaeGateSheet = {
  variant: UaeNextStep;
  level: UaeAccessLevel | null;
  trigger: UaeGateTrigger;
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  ctaRoute: string;
  limitLine: { key: string; count: number } | null;
  analyticsSubject: string | null;
};

const isLevel = (v: unknown): v is UaeAccessLevel =>
  typeof v === 'string' && (UAE_ACCESS_LEVELS as readonly string[]).includes(v);
const isNextStep = (v: unknown): v is UaeNextStep => v === 'verify_id' || v === 'join';

type BillingMeLike =
  | {
      market?: string | null;
      uaeAccessLevel?: string | null;
      nextStep?: string | null;
      showPricing?: boolean | null;
      isTeamOwner?: boolean | null;
    }
  | null
  | undefined;

// UAE state comes only from /api/billing/me — the web never derives the level.
// UAE surfaces follow the server's showPricing: false (a UAE-market partner may still get pricing).
export const readUaeProgramme = (me: BillingMeLike): UaeProgramme => {
  if (!me) return NOT_UAE;
  if (me.market !== 'UAE' || me.showPricing === true) return { ...NOT_UAE, loaded: true };
  return {
    loaded: true,
    isUae: true,
    level: isLevel(me.uaeAccessLevel) ? me.uaeAccessLevel : null,
    nextStep: isNextStep(me.nextStep) ? me.nextStep : null,
    showPricing: false,
    ...(me.isTeamOwner === true ? { teamOwner: true } : {}),
  };
};

type ErrorData = Record<string, unknown>;

// H3 errors surface as err.data.data (FetchError body) or err.data (NuxtError / plain body).
const readErrorData = (error: unknown): ErrorData | null => {
  if (!error || typeof error !== 'object') return null;
  const e = error as { data?: unknown; response?: { _data?: unknown } };
  const candidates = [e.data, e.response?._data];
  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    const inner = (c as { data?: unknown }).data;
    if (inner && typeof inner === 'object' && 'reason' in inner) return inner as ErrorData;
    if ('reason' in c) return c as ErrorData;
  }
  return null;
};

export const UAE_GATE_REASONS = [
  'uae_level_required',
  'uae_limit_reached',
  'uae_no_subscription',
  'emirates_id_required',
] as const;

export const parseUaeGateError = (error: unknown): UaeGateRequest | null => {
  const data = readErrorData(error);
  const reason = data?.reason;
  if (reason === 'uae_limit_reached') {
    const feature = data?.feature === 'ai' || data?.feature === 'marketing' ? data.feature : null;
    return {
      trigger: 'limit_reached',
      nextStep: isNextStep(data?.nextStep) ? data.nextStep : 'join',
      feature,
      limit: typeof data?.limit === 'number' ? data.limit : null,
    };
  }
  if (reason === 'uae_level_required') {
    return {
      trigger: 'locked_action',
      nextStep: isNextStep(data?.nextStep) ? data.nextStep : null,
      level: isLevel(data?.currentLevel) ? data.currentLevel : null,
      action: typeof data?.action === 'string' ? data.action : null,
    };
  }
  // Exclusive / distress actions (hold, enquire, sales kit) before the Emirates ID is approved.
  if (reason === 'emirates_id_required') {
    return {
      trigger: 'locked_action',
      nextStep: 'verify_id',
      action: typeof data?.feature === 'string' ? data.feature : null,
    };
  }
  if (reason === 'uae_no_subscription') return { trigger: 'locked_action', nextStep: null, action: 'subscribe' };
  return null;
};

const LIMIT_LINE_KEYS: Record<UaeGateFeature, string> = {
  ai: 'billing.uaeGate.limitAi',
  marketing: 'billing.uaeGate.limitPosts',
};

// Returns null when there is no next step (e.g. a JOINED agent) — nothing to show.
export const resolveUaeGateSheet = (request: UaeGateRequest, programme: UaeProgramme): UaeGateSheet | null => {
  const level = request.level ?? programme.level;
  const variant = request.nextStep ?? programme.nextStep ?? (level === 'SIGNED_UP' ? 'verify_id' : null);
  if (!variant) return null;
  const base = variant === 'verify_id' ? 'billing.uaeGate.verify' : 'billing.uaeGate.join';
  const limitLine =
    request.trigger === 'limit_reached' && request.feature && typeof request.limit === 'number'
      ? { key: LIMIT_LINE_KEYS[request.feature], count: request.limit }
      : null;
  return {
    variant,
    level,
    trigger: request.trigger,
    titleKey: `${base}.title`,
    bodyKey: `${base}.body`,
    ctaKey: `${base}.cta`,
    ctaRoute: variant === 'verify_id' ? UAE_VERIFY_ID_ROUTE : UAE_JOIN_ROUTE,
    limitLine,
    analyticsSubject: request.feature ?? request.action ?? null,
  };
};

// Locale prefixes (prefix_except_default, e.g. /he/saas) are stripped before matching.
// ID-241: a UAE Team owner manages their seats on the team page.
export const UAE_TEAM_OWNER_ROUTE = '/profile/team';

export const isUaeBlockedRoute = (path: string, opts: { teamOwner?: boolean } = {}): boolean => {
  const bare = path.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
  if (opts.teamOwner && (bare === UAE_TEAM_OWNER_ROUTE || bare.startsWith(`${UAE_TEAM_OWNER_ROUTE}/`))) return false;
  return UAE_BLOCKED_ROUTE_PREFIXES.some((prefix) => bare === prefix || bare.startsWith(`${prefix}/`));
};

export type UaeNextStepCopy = { titleKey: string; bodyKey: string; ctaKey: string };

// Copy for inline locks / upsell teasers shown to UAE agents instead of plan names.
export const uaeNextStepCopy = (programme: UaeProgramme): UaeNextStepCopy => {
  const verify = programme.nextStep === 'verify_id' || (!programme.nextStep && programme.level !== 'VERIFIED');
  const base = verify ? 'billing.uaeGate.verify' : 'billing.uaeGate.join';
  return { titleKey: `${base}.title`, bodyKey: `${base}.body`, ctaKey: `${base}.cta` };
};
